import {
  RiArrowGoBackLine,
  RiArrowGoForwardLine,
  RiCheckFill,
  RiCheckboxBlankLine,
  RiCircleLine,
  RiCursorFill,
  RiEraserFill,
  RiLoader3Line,
  RiPencilLine,
  RiText
} from '@remixicon/react'
import { useMutation } from '@tanstack/react-query'
import { Canvas, Circle, FabricImage, PencilBrush, Rect, Textbox } from 'fabric'
import { useEffect, useLayoutEffect, useState } from 'react'
import { toast } from 'sonner'
import { useDebouncedCallback } from 'use-debounce'

import { CanvasColorPicker } from './color-picker'
import { useCanvasEvents } from './hooks/use-canvas-events'
import { useCanvasHistory } from './hooks/use-canvas-history'
import { useCanvasTools } from './hooks/use-canvas-tools'
import { ToolbarButton } from './toolbar-button'
import { itemsService } from '@/api/items/items-services'
import type { Detail } from '@/api/orders/orders-types'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { useImageCacheStore } from '@/store/image-cache'
import { getDrawingUrl, getVariobendFile, isAdditionalFileStale, withCurrentDrawing } from '@/utils/details'
import { dataURLtoBlob } from '@/utils/files'

const DEFAULT_COLOR = '#0831FF'
const DEFAULT_FONT_FAMILY = 'Roboto'

interface ImageCanvasToolbarProps {
  className?: string
  data: Detail
  canvas: Canvas
  viewOriginal: boolean
}

export function ImageCanvasToolbar({ className, canvas, data, viewOriginal }: ImageCanvasToolbarProps) {
  const [selectedObject, setSelectedObject] = useState<any>(null)
  const [objectColor, setObjectColor] = useState(DEFAULT_COLOR)
  const [backgroundImage, setBackgroundImage] = useState<any>(null)

  const { handleRedo, handleUndo, saveState, canRedo, canUndo } = useCanvasHistory({
    canvas
  })

  const { activeTool, handleToolSelect, applyTool, setActiveTool } = useCanvasTools(
    canvas,
    objectColor,
    saveState
  )

  const newestVariobendImage = getVariobendFile({ files: data.variobend_files })

  useCanvasEvents(canvas, handleObjectSelect, setSelectedObject)

  useLayoutEffect(() => {
    const loadImages = async () => {
      if (!canvas) return

      canvas.preserveObjectStacking = true

      const currentImage = data.additional_files

      const lastJSONImage = currentImage?.[currentImage?.length - 1]?.additional_info

      const drawingUrl = getDrawingUrl(newestVariobendImage)

      const addDrawing = async () => {
        if (backgroundImage) canvas.remove(backgroundImage)

        // const proxyUrl = 'https://corsproxy.io/?'

        const bgImg = await FabricImage.fromURL(drawingUrl, {
          crossOrigin: 'anonymous'
        })

        bgImg.selectable = false
        bgImg.evented = false
        bgImg.scale(0.9)

        setBackgroundImage(bgImg)
        canvas.add(bgImg)
        canvas.sendObjectToBack(bgImg)
        canvas.centerObject(bgImg)
      }

      if (lastJSONImage) {
        // A failed load (link gone) leaves the canvas empty; the drawing is added below.
        await canvas.loadFromJSON(withCurrentDrawing(lastJSONImage, newestVariobendImage)).catch(() => {})
        // Annotations were drawn on an older drawing: swap in the current one so a re-save syncs them.
        if (isAdditionalFileStale(currentImage[currentImage.length - 1], newestVariobendImage)) {
          const bgImg = canvas.getObjects().find((obj) => obj.type === 'image') as FabricImage | undefined
          // Load failure keeps the old drawing rather than aborting the setup below.
          await bgImg?.setSrc(drawingUrl, { crossOrigin: 'anonymous' }).catch(() => {})
        }
        // Saved without its drawing (it failed to load at the time): put it back under the annotations.
        if (newestVariobendImage && !canvas.getObjects().some((obj) => obj.type === 'image')) {
          await addDrawing().catch(() => {})
        }
        canvas.getObjects().forEach((obj) => {
          if (viewOriginal && obj.type !== 'image') {
            obj.visible = false
          }

          if (obj.type === 'path' || obj.type === 'rect' || obj.type === 'circle') {
            obj.erasable = true
          }

          if (obj.type === 'image') {
            obj.selectable = false
            obj.evented = false
          }
        })
        canvas.renderAll()
      } else {
        await addDrawing()
      }
    }
    loadImages()
  }, [canvas, newestVariobendImage, viewOriginal])

  useEffect(() => {
    applyTool()
  }, [canvas, activeTool, objectColor, viewOriginal])

  function handleObjectSelect(object: any) {
    if (!object) return
    if (object.stroke && typeof object.stroke === 'string') {
      setObjectColor(object.stroke)
    }
    setSelectedObject(object)
  }

  const handleColorChange = useDebouncedCallback((color: string) => {
    setObjectColor(color)
    if (selectedObject) {
      if (selectedObject instanceof Textbox) {
        selectedObject.set({ fill: color })
      } else {
        selectedObject.set({ stroke: color })
      }
      canvas?.renderAll()
      saveState()
    }
    if (canvas?.freeDrawingBrush instanceof PencilBrush) {
      canvas.freeDrawingBrush.color = color
    }
  }, 200)

  function handleAddCircle() {
    if (canvas) {
      handleToolSelect(null)
      const circle = new Circle({
        radius: 50,
        erasable: true,
        top: 100,
        left: 100,
        stroke: objectColor,
        strokeWidth: 2,
        fill: 'transparent'
      })
      canvas.add(circle)
      canvas.setActiveObject(circle)
      canvas.requestRenderAll()
    }
  }

  function handleAddRect() {
    if (canvas) {
      handleToolSelect(null)
      const rect = new Rect({
        width: 100,
        height: 100,
        erasable: true,
        top: 100,
        left: 100,
        stroke: objectColor,
        strokeWidth: 2,
        fill: 'transparent'
      })
      canvas.add(rect)
      canvas.setActiveObject(rect)
      canvas.requestRenderAll()
    }
  }

  function handleAddText() {
    if (canvas) {
      handleToolSelect(null)
      const textbox = new Textbox('Add text here', {
        fontFamily: DEFAULT_FONT_FAMILY,
        fontSize: 20,
        fill: objectColor,
        editable: true,
        selectable: true,
        width: 150,
        top: 100,
        left: 100,
        lockScalingX: true,
        lockScalingY: true,
        lockSkewingX: true,
        lockSkewingY: true,
        lockScalingFlip: true
      })
      textbox.enterEditing()
      canvas.add(textbox)
      textbox.selectAll()
      canvas.setActiveObject(textbox)
      canvas.requestRenderAll()
    }
  }

  useEffect(() => {
    if (viewOriginal) {
      canvas.selection = false
      canvas.off()
      setActiveTool('pointer')

      canvas.getObjects().forEach((obj) => {
        if (obj.type !== 'image') {
          obj.visible = false
        }
      })
    }
  }, [viewOriginal, canvas])

  const addItemImageMutation = useMutation({
    mutationFn: ({ autoid, payload }: { autoid: string; payload: FormData }) => {
      return itemsService.addImage(autoid, payload)
    },

    onSuccess: (_, { autoid }) => {
      useImageCacheStore.getState().bust(autoid)
    },

    meta: {
      invalidatesQuery: ['orders', 'items', 'timeline-orders'],
      successMessage: 'Image added successfully'
    }
  })

  function handleAddItemFile() {
    if (!canvas || !data?.autoid) {
      toast.error('Cannot upload: missing canvas, image, or detail ID')
      return
    }

    // Without its drawing the saved PNG would be a blank canvas.
    if (newestVariobendImage && !canvas.getObjects().some((obj) => obj.type === 'image')) {
      toast.error('Drawing did not load — reopen the editor and try again')
      return
    }

    canvas.renderAll()

    const dataURL = canvas.toDataURL({
      multiplier: 1,
      format: 'png'
    })

    const blobData = dataURLtoBlob(dataURL)

    const formData = new FormData()
    formData.append('file', blobData, 'canvas-image.png')
    formData.append('additional_info', JSON.stringify(canvas.toJSON()))

    addItemImageMutation.mutate({
      autoid: data?.autoid!,
      payload: formData
    })

    canvas.renderAll()
  }

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Backspace' || (e.key === 'Delete' && !viewOriginal)) {
        if (!selectedObject) return

        if (selectedObject instanceof Textbox && selectedObject.isEditing) {
          return
        }

        e.preventDefault()

        canvas?.remove(selectedObject)
        setSelectedObject(null)
        canvas?.discardActiveObject()
        canvas?.requestRenderAll()

        saveState()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [canvas, selectedObject, saveState, viewOriginal])

  useEffect(() => {
    document.body.style.cursor = activeTool === 'pointer' ? 'grab' : 'default'
    return () => {
      document.body.style.cursor = 'default'
    }
  }, [activeTool])

  if (viewOriginal) {
    return null
  }

  return (
    <div className={cn('bg-white-100 shadow-md-down rounded-lg border px-2.5 py-1.5', className)}>
      <div className='flex items-center gap-2.5'>
        <ToolbarButton
          onClick={() => handleToolSelect('pointer')}
          active={activeTool === 'pointer'}
          aria-label='Pointer'
        >
          <RiCursorFill />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => handleToolSelect('draw')}
          active={activeTool === 'draw'}
          aria-label='Draw'
        >
          <RiPencilLine />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => handleToolSelect('erase')}
          active={activeTool === 'erase'}
          aria-label='Erase'
        >
          <RiEraserFill />
        </ToolbarButton>
        <ToolbarButton
          onClick={handleAddCircle}
          aria-label='Add Circle'
        >
          <RiCircleLine />
        </ToolbarButton>
        <ToolbarButton
          onClick={handleAddRect}
          aria-label='Add Rectangle'
        >
          <RiCheckboxBlankLine />
        </ToolbarButton>
        <ToolbarButton
          onClick={handleAddText}
          aria-label='Add Text'
        >
          <RiText />
        </ToolbarButton>
        <Separator
          orientation='vertical'
          className='h-5'
        />
        <ToolbarButton
          disabled={!canUndo}
          onClick={handleUndo}
          aria-label='Undo'
        >
          <RiArrowGoBackLine />
        </ToolbarButton>
        <ToolbarButton
          disabled={!canRedo}
          onClick={handleRedo}
          aria-label='Redo'
        >
          <RiArrowGoForwardLine />
        </ToolbarButton>
        <Separator
          orientation='vertical'
          className='h-5'
        />
        <CanvasColorPicker
          onChange={handleColorChange}
          value={objectColor}
        />
        <Separator
          orientation='vertical'
          className='h-5'
        />
        <Button
          disabled={addItemImageMutation.isPending}
          size='sm'
          onClick={handleAddItemFile}
        >
          {addItemImageMutation.isPending ? <RiLoader3Line className='animate-spin' /> : <RiCheckFill />}
          <span>Save</span>
        </Button>
      </div>
    </div>
  )
}
